/**
 * Outer-boundary helpers for NAV-07 (walkable-area exit feedback).
 *
 * The outerBoundary is a single large AABB defining the walkable bowl. When the
 * player crosses its edge (with negative padding = interior margin), WalkRig:
 *   1. sets useNavStore.boundaryHit = true → BoundaryFeedback vignette appears
 *   2. lerps the player position toward the boundary center via pushTowardCenter
 *
 * Pure math — no framework deps. Mirrors aabb.ts structure.
 */

import type { AABB, Vec3 } from './aabb';
import { pointInsideAABB } from './aabb';

/**
 * Test whether a point is INSIDE the outer boundary with an interior margin.
 *
 * `padding` is typically NEGATIVE here (e.g., -0.5 = stay 0.5m inside the edge),
 * which prevents jittery oscillation at the exact boundary line.
 */
export function isInsideBoundary(
  point: Vec3,
  boundary: AABB,
  padding = -0.5
): boolean {
  return pointInsideAABB(point, boundary, padding);
}

/**
 * Gently push `point` toward the center of `boundary`.
 *
 * Called per-frame when the player is outside the walkable bowl to redirect them.
 * `strength` is a linear-interp factor (0-1); small values (~0.05) feel soft and
 * natural. 1.0 would teleport the player to the center (avoid).
 *
 * Returns a new Vec3 — does not mutate input.
 */
export function pushTowardCenter(
  point: Vec3,
  boundary: AABB,
  strength = 0.05
): Vec3 {
  const cx = (boundary.min[0] + boundary.max[0]) * 0.5;
  const cy = (boundary.min[1] + boundary.max[1]) * 0.5;
  const cz = (boundary.min[2] + boundary.max[2]) * 0.5;
  return [
    point[0] + (cx - point[0]) * strength,
    point[1] + (cy - point[1]) * strength,
    point[2] + (cz - point[2]) * strength,
  ];
}
